import { dateToYMD } from '@shared/utils/date';
import { REGISTRATION_FIELDS } from '../registrationFields.config';
import type {
  TRegistrationDateKey,
  TRegistrationDetails,
  TRegistrationFormValues,
  TRegistrationUpdate,
} from '../types';

export const REGISTRATION_DATE_KEYS: readonly TRegistrationDateKey[] = [
  'validFromDate',
  'validUntilDate',
  'reminderDate',
];

// Stored values can predate a field's text type (e.g. imported numbers): show them, but never rewrite them unedited.
export const toMetadataText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// Dates are UTC midnights; their calendar day is the ISO date part, never a local-time conversion.
export const toRegistrationFormValues = (details: TRegistrationDetails): TRegistrationFormValues => ({
  status: details.status,
  validFromDate: dateToYMD(details.validFromDate) || null,
  validUntilDate: dateToYMD(details.validUntilDate) || null,
  reminderDate: dateToYMD(details.reminderDate) || null,
  metadata: Object.fromEntries(REGISTRATION_FIELDS.map(({ key }) => [key, toMetadataText(details.metadata[key])])),
});

// Diffs against the latest details and sends only the changes: omitted fields and metadata keys, including keys
// no longer configured, stay as stored. An emptied date or text sends `null`, which clears it.
export const buildRegistrationUpdate = (
  details: TRegistrationDetails,
  values: TRegistrationFormValues
): TRegistrationUpdate => {
  const current = toRegistrationFormValues(details);
  const update: TRegistrationUpdate = {};

  if (values.status !== current.status) update.status = values.status;
  REGISTRATION_DATE_KEYS.forEach((key) => {
    if (values[key] !== current[key]) update[key] = values[key] || null;
  });

  const metadata: Record<string, string | null> = {};
  Object.entries(values.metadata).forEach(([key, value]) => {
    const text = value.trim();
    if (value !== current.metadata[key] && text !== current.metadata[key]) metadata[key] = text || null;
  });
  if (Object.keys(metadata).length) update.metadata = metadata;

  return update;
};
